import { buildWidgetStateFixture } from '@loyalty/editor-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorService } from '../services/editor-service';
import { useAccounts } from '../../accounts/AccountsContext';
import { useApi } from '../../shared/api-client/ApiClientProvider';
import { useConfiguration } from '../../ConfigurationContext';
import { useCurrencies } from '../../currency/CurrenciesContext';
import { useCurrentTemplate } from './use-templates';
import { useEditorStore } from '../store';
import { useFeatures } from '../../features/FeaturesContext';
import { useFonts } from './use-fonts';
import { useParticipantUrl } from './use-participant-url';
import { usePortalUrl } from '../../shared/open-portal-with-internal-auth';
import { useProgram } from '../../loyalty/ProgramContext';
import { useReferralConfig } from '../../referral/ReferralConfigContext';

export function useEditorService() {
  const { i18n, t } = useTranslation();

  const { currentAccount } = useAccounts();
  const { program } = useProgram();
  const { currencies } = useCurrencies();
  const { referralConfig } = useReferralConfig();
  const apis = useApi();
  const { features } = useFeatures();
  const { qualifioApiUrl } = useConfiguration();
  const { getPortalUrl } = usePortalUrl(program.id);

  // React Query hooks
  const { data: template } = useCurrentTemplate();
  const { data: fonts } = useFonts();
  const { data: participantUrl } = useParticipantUrl();

  // Zustand store
  const editorService = useEditorStore(state => state.editorService);
  const initializeEditorService = useEditorStore(state => state.initializeEditorService);
  const destroyEditorService = useEditorStore(state => state.destroyEditorService);
  const setEditorState = useEditorStore(state => state.setEditorState);
  const setSelectedComponent = useEditorStore(state => state.setSelectedComponent);
  const markAsDirty = useEditorStore(state => state.markAsDirty);
  const markAsClean = useEditorStore(state => state.markAsClean);
  const customCode = useEditorStore(state => state.customCode);
  const editorTheme = useEditorStore(state => state.editorTheme);
  const getTemplateJSON = useEditorStore(state => state.getTemplateJSON);
  const openModal = useEditorStore(state => state.openModal);
  const closeModal = useEditorStore(state => state.closeModal);

  useEffect(() => {
    const { schema, theme, styles } = template || {
      schema: { components: {} },
      components: [],
    };

    const service = new EditorService(
      {
        apis,
        schema: schema.components as any,
        i18n,
        t,
        programId: program.id,
        participantUrl: participantUrl ?? '',
        customCode,
        styles,
        features,
      },
      {
        qualifioApiUrl,
        accountId: currentAccount.id,
        programId: program.id,
        languages: program.languages,
        initialLocale: program.languages[0],
        currencies,
        referralConfig,
        apis,
        t,
        openModal: getContent => {
          openModal(getContent({ closeModal }));
        },
        theme: editorTheme || theme,
        fonts,
        widgetState: buildWidgetStateFixture(
          currencies.filter(c => !c.deletedAt),
          referralConfig,
          getPortalUrl(),
          program.languages,
        ),
      },
    );

    service.on('load', () => {
      initializeEditorService(service, program.languages[0]);
    });

    return () => {
      destroyEditorService();
    };
  }, []);

  //
  useEffect(() => {
    if (editorService) {
      // Event handlers
      editorService.on('component:selected', model => setSelectedComponent(model));
      editorService.on('component:add', () => markAsDirty());
      editorService.on('component:update', () => markAsDirty());
      editorService.on('component:clone', () => markAsDirty());
      editorService.on('component:remove', event => {
        if (
          editorService.editor.getSelectedAll().find((c: { cid: string }) => c.cid === event.cid)
        ) {
          setSelectedComponent(undefined);
          markAsDirty();
        }
      });

      // GrapesJS fills an about:blank iframe with document.write. Safari treats that
      // parent as opaque and omits Referer on nested YouTube embeds (Error 153).
      // replaceState gives the canvas document an HTTPS URL so Safari sends a Referer,
      // without navigating the iframe (iframe.src reloads it and breaks drag-and-drop).
      // https://gitlab.service.ops.internal.qualif.io/qualifioapp/product/support/-/work_items/487
      editorService.editor.Canvas.getWindow().history.replaceState(null, '', window.location.href);
    }
  }, [editorService]);

  // Sync template changes to editor
  useEffect(() => {
    if (template && editorService) {
      setEditorState(template);
      markAsClean();
    }
  }, [template, editorService, setEditorState, markAsClean, getTemplateJSON]);

  // Sync fonts changes to editor
  useEffect(() => {
    if (fonts && editorService) {
      editorService.setFonts(fonts);
    }
  }, [fonts, editorService]);
}
